/**
 * A tool named without its arguments, as an automation check names it: its integration's logo and its
 * name, with what it is on hover and a cross while editable. Styled as the answer key's tool call chip.
 */

import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { IntegrationLogo } from '@/modules/integrations';
import { humanize } from '@/modules/simulator/ui/text';
import type { ToolDefinitionAction } from '@/services/toolDefinition.service';
import { RemoveButton } from '../ui/remove-button';
import { resolveCall } from './tool-call';
import { toolAccess } from './tool-filters';

interface ToolRefChipProps {
	tool: { name: string; provider: string | null };
	definitions: ToolDefinitionAction[];
	/** The chip's border and name in a finding's colour: a tool missing or called when it must not be. */
	tone?: 'bad';
	onRemove?: () => void;
}

export const ToolRefChip = ({ tool, definitions, tone, onRemove }: ToolRefChipProps) => {
	const { definition, sharedBy } = resolveCall(tool, definitions);
	const name = definition?.label ?? humanize(tool.name);
	const provider = tool.provider ?? (definition?.provider || null);
	const integration = provider ? humanize(provider) : sharedBy.length > 0 ? sharedBy.map(humanize).join(' or ') : 'Any integration';
	const about = [integration, definition && (toolAccess(definition.riskLevel) === 'write' ? 'writes' : 'reads')]
		.filter(Boolean)
		.join(' · ');

	return (
		<Tooltip>
			<TooltipTrigger asChild>
				<span
					className={cn(
						'inline-flex h-7 max-w-full items-center gap-1.5 rounded-full border bg-white pl-1.5 text-[13px] leading-4.5',
						tone === 'bad' ? 'border-red-300' : 'border-gray-500',
						onRemove ? 'pr-1' : 'pr-3'
					)}
				>
					<IntegrationLogo provider={provider} className='size-4' decorative />
					<span className={cn('truncate', tone === 'bad' ? 'text-red-700' : 'text-text-body')}>{name}</span>
					{onRemove && <RemoveButton label={`Remove ${name}`} onClick={onRemove} />}
				</span>
			</TooltipTrigger>
			<TooltipContent side='bottom' align='start' className='max-w-sm'>
				<p className='font-mono break-all text-text-body'>{tool.name}</p>
				<p className='mt-1'>{about}</p>
			</TooltipContent>
		</Tooltip>
	);
};
